import { useQuery } from '@apollo/client';
import { transformError } from '@core/adapters/errors/transformError';
import { EXTENDED_GRAPHQL_TIMEOUT_MS } from '@core/services/constants';
import { useMemo, useCallback } from 'react';

import { GET_AUDIT_ITEMS } from '../graphql/queries/getAuditItems';

interface AuditItemsInput {
  auditSessionId: string;
  status?: string;
  label?: string;
  severity?: number;
  auditorId?: string;
  hasMultipleDecisions?: boolean;
  hasConflictingDecisions?: boolean;
  violatesPolicy?: boolean;
  page?: number;
  pageSize?: number;
}

interface GetAuditItemsResponse {
  auditItems: {
    items: Array<{
      id: string;
      type: string;
      createdBy: string;
      createdAt: string;
      lastModifiedBy: string;
      lastModifiedAt: string;
      auditSessionId: string;
      status: string;
      position: number;
      decisionsCount: number;
      selectedDecisionId?: string;
      contentSnapshot: {
        audioFileUrl?: string;
        transcription?: string;
        language?: string;
        topic?: string;
        contentType?: string;
        classificationResults?: Array<{
          name: string;
          severity: number;
        }>;
        policies?: Array<{
          id: string;
          name: string;
          description: string;
          communityId: string;
          isEnabled: boolean;
          labels: Array<{
            labelId: string;
            severity?: number;
          }>;
        }>;
        slmModelVersion?: string;
        slmPromptVersion?: string;
        quicktxtModelVersion?: string;
        metadata?: any; // eslint-disable-line @typescript-eslint/no-explicit-any
      };
      kustoReference: {
        communityId: string;
        subjectId: string;
        timestamp: string;
      };
    }>;
    totalCount: number;
    page: number;
    pageSize: number;
  };
}

/**
 * Hook for fetching audit items for a session
 * Part of the 4-query architecture - fetches only items data, not decisions
 */
export function useGetAuditItems(sessionId: string | undefined, itemsInput?: Omit<AuditItemsInput, 'auditSessionId'>) {
  const variables = useMemo(
    () => ({
      input: itemsInput
        ? {
            auditSessionId: sessionId,
            ...itemsInput,
            // Ensure we always have a pageSize, defaulting to 100
            pageSize: itemsInput.pageSize || 100,
          }
        : {
            auditSessionId: sessionId,
            pageSize: 100,
          },
    }),
    [sessionId, itemsInput]
  );

  const { data, loading, error, refetch } = useQuery<GetAuditItemsResponse>(GET_AUDIT_ITEMS, {
    variables,
    skip: !sessionId,
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network', // Instant cache + fresh network data on first load
    nextFetchPolicy: 'cache-first', // Subsequent queries use cache-first
    notifyOnNetworkStatusChange: false, // Don't show loading during background refetches
    context: { timeout: EXTENDED_GRAPHQL_TIMEOUT_MS },
  });

  // Extract items data
  const result = useMemo(() => {
    if (!data?.auditItems) {
      return { items: [], itemsResponse: null };
    }

    return {
      items: data.auditItems.items,
      itemsResponse: data.auditItems,
    };
  }, [data]);

  // Transform error using adapter pattern
  const domainError = useMemo(() => {
    if (!error) return undefined;

    const mappedError = transformError(error);

    return {
      ...mappedError,
      onRetry: () => refetch(),
    };
  }, [error, refetch]);

  // Create a refetch function that accepts pagination and filter parameters
  const refetchWithPagination = useCallback(
    (options?: {
      page?: number;
      pageSize?: number;
      // Filter parameters
      status?: string;
      label?: string;
      severity?: number;
      auditorId?: string;
      hasMultipleDecisions?: boolean;
      hasConflictingDecisions?: boolean;
      violatesPolicy?: boolean;
    }) => {
      const newVariables = {
        input: {
          ...variables.input,
          auditSessionId: sessionId,
          ...options,
        },
      };

      return refetch(newVariables);
    },
    [refetch, variables, sessionId]
  );

  return {
    items: result?.items || [],
    itemsResponse: result?.itemsResponse || null,
    loading,
    error: domainError,
    refetch: refetchWithPagination,
  };
}
